import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { Suppliers } from 'tilby-models';

export type NewMovementMode = 'supplier_order' | 'goods_delivery' | 'goods_delivery_adjustment';
export type MovementType = 'load' | 'unload' | 'setup';

export interface MovementCause {
    type: MovementType;
    typeName: string;
    value: string;
    name: string;
}

export type MovementEntryType = 'item' | 'raw_material';

// prodotto (anche una singola combinazione) o materia prima selezionabile in una riga
export type MovementEntry = Record<string, any> & {
    id: number;
    _entryType: MovementEntryType;
    _name: string;
    combination_id?: number | null;
};

export interface StockLocationOption {
    id: number;
    name: string;
}

// parametri con cui la pagina viene aperta
export interface NewMovementParams {
    mode?: NewMovementMode | null;
    movements?: any[] | any | null;
    stockLocationId?: number | null;
}

export type NewMovementRowForm = {
    entry: FormControl<MovementEntry | string | null>;
    quantity: FormControl<number | null>;
    price: FormControl<number | null>;
    perc_discount: FormControl<number | null>;
    // quantità attesa nel ricevimento merce
    oldQuantity: FormControl<number | null>;
};

export type NewMovementForm = {
    movementCause: FormControl<MovementCause | null>;
    sourceLocationId: FormControl<number | null>;
    targetLocationId: FormControl<number | null>;
    supplier: FormControl<Suppliers | string | null>;
    date: FormControl<string | null>;
    supplierOrder: FormControl<string | null>;
    notes: FormControl<string | null>;
    rows: FormArray<FormGroup<NewMovementRowForm>>;
};

export type NewMovementFormGroup = FormGroup<NewMovementForm>;

export interface NewMovementRowValue {
    entry: MovementEntry | string | null;
    quantity: number | null;
    price: number | null;
    perc_discount: number | null;
    oldQuantity: number | null;
}

// movimento pronto per l'invio, con l'indice della riga del form da cui deriva
export interface PreparedMovement {
    rowIndex: number;
    rowValue: NewMovementRowValue;
    payload: Record<string, any>;
}

export type NewMovementErrorCode =
    'MISSING_MOVEMENT_CAUSE' | 'MISSING_LOCATION_TARGET' | 'MISSING_ENTRY' | 'MISSING_QUANTITY';

const NEW_MOVEMENT_ERROR_CODES: readonly NewMovementErrorCode[] =
    ['MISSING_MOVEMENT_CAUSE', 'MISSING_LOCATION_TARGET', 'MISSING_ENTRY', 'MISSING_QUANTITY'];

export const isNewMovementErrorCode = (error: unknown): error is NewMovementErrorCode =>
    NEW_MOVEMENT_ERROR_CODES.includes(error as NewMovementErrorCode);

export const STOCK_TRANSFER_CAUSE = 'stock_transfer';
export const SALES_LOCATION_ID = 1;
export const SUPPLIERS_ORDERS_LOCATION_ID = 2;

export const MOVEMENT_CAUSE_DEFINITIONS: { type: MovementType; typeNameKey: string; value: string; nameKey: string }[] = [
    { type: 'load', typeNameKey: 'STOCK.TOPBAR_STOCK.LOAD', value: 'buy', nameKey: 'STOCK.CAUSES.BUY' },
    { type: 'load', typeNameKey: 'STOCK.TOPBAR_STOCK.LOAD', value: 'refund', nameKey: 'STOCK.CAUSES.REFUND' },
    { type: 'load', typeNameKey: 'STOCK.TOPBAR_STOCK.LOAD', value: 'replacement', nameKey: 'STOCK.CAUSES.REPLACEMENT' },
    { type: 'load', typeNameKey: 'STOCK.TOPBAR_STOCK.LOAD', value: 'production', nameKey: 'STOCK.CAUSES.PRODUCTION' },
    { type: 'load', typeNameKey: 'STOCK.TOPBAR_STOCK.LOAD', value: 'transfer', nameKey: 'STOCK.CAUSES.TRANSFER' },
    { type: 'load', typeNameKey: 'STOCK.TOPBAR_STOCK.LOAD', value: 'positive_adjustment', nameKey: 'STOCK.CAUSES.POSITIVE_ADJUSTMENT' },
    { type: 'load', typeNameKey: 'STOCK.TOPBAR_STOCK.LOAD', value: 'supplier_order', nameKey: 'STOCK.CAUSES.SUPPLIER_ORDER' },
    { type: 'unload', typeNameKey: 'STOCK.TOPBAR_STOCK.UNLOAD', value: 'sale', nameKey: 'STOCK.CAUSES.SALE' },
    { type: 'unload', typeNameKey: 'STOCK.TOPBAR_STOCK.UNLOAD', value: 'gift', nameKey: 'STOCK.CAUSES.GIFT' },
    { type: 'unload', typeNameKey: 'STOCK.TOPBAR_STOCK.UNLOAD', value: 'internal_use', nameKey: 'STOCK.CAUSES.INTERNAL_USE' },
    { type: 'unload', typeNameKey: 'STOCK.TOPBAR_STOCK.UNLOAD', value: 'damage', nameKey: 'STOCK.CAUSES.DAMAGE' },
    { type: 'unload', typeNameKey: 'STOCK.TOPBAR_STOCK.UNLOAD', value: 'theft', nameKey: 'STOCK.CAUSES.THEFT' },
    { type: 'unload', typeNameKey: 'STOCK.TOPBAR_STOCK.UNLOAD', value: 'waste', nameKey: 'STOCK.CAUSES.WASTE' },
    { type: 'unload', typeNameKey: 'STOCK.TOPBAR_STOCK.UNLOAD', value: 'supplier_return', nameKey: 'STOCK.CAUSES.SUPPLIER_RETURN' },
    { type: 'unload', typeNameKey: 'STOCK.TOPBAR_STOCK.UNLOAD', value: 'transfer', nameKey: 'STOCK.CAUSES.TRANSFER' },
    { type: 'unload', typeNameKey: 'STOCK.TOPBAR_STOCK.UNLOAD', value: 'move', nameKey: 'STOCK.CAUSES.MOVE' },
    { type: 'unload', typeNameKey: 'STOCK.TOPBAR_STOCK.UNLOAD', value: 'replacement', nameKey: 'STOCK.CAUSES.REPLACEMENT' },
    { type: 'unload', typeNameKey: 'STOCK.TOPBAR_STOCK.UNLOAD', value: 'negative_adjustment', nameKey: 'STOCK.CAUSES.NEGATIVE_ADJUSTMENT' },
    { type: 'unload', typeNameKey: '', value: STOCK_TRANSFER_CAUSE, nameKey: 'STOCK.CAUSES.STOCK_TRANSFER' },
    { type: 'setup', typeNameKey: '', value: 'setup', nameKey: 'STOCK.TOPBAR_STOCK.SETUP' },
];
